<script>
    import { currentUser } from './store.js';
    import { api, billingAPI, dataAPI } from './api.js';
    import { notificationService } from './notificationService.js';

    let activeTab = 'profile';
    let saving = false;
    let message = { type: '', text: '' };

    // Datos & Créditos
    let billingBalance = null;
    let billingTxs = { items: [], total: 0, page: 1, per_page: 20 };
    let importResult = null;
    let importing = false;
    let exporting = '';

    // Recargas y paquetes por SINPE
    let paymentInfo = null;
    let myRequests = [];
    let topupCrc = '';
    let topupNote = '';
    let requestPackageId = '';
    let requestingKind = '';

    async function loadDataTab() {
        try { billingBalance = await billingAPI.balance(); } catch { billingBalance = null; }
        try { paymentInfo = await billingAPI.paymentInfo(); } catch { paymentInfo = null; }
        try { myRequests = await billingAPI.requests(); } catch { myRequests = []; }
        await loadTxPage(1);
    }
    async function loadTxPage(page) {
        try { billingTxs = await billingAPI.transactions(page); } catch {}
    }

    $: sellablePackages = (paymentInfo?.packages || []).filter(p => (p.price_crc || 0) > 0);
    $: waReceiptLink = paymentInfo
        ? `https://wa.me/${paymentInfo.payment_whatsapp}?text=${encodeURIComponent('Hola, adjunto el comprobante SINPE de mi pago de Mangu.')}`
        : '#';

    async function submitTopupRequest() {
        requestingKind = 'topup';
        try {
            const res = await billingAPI.createRequest({
                kind: 'topup', amount_crc: Number(topupCrc), note: topupNote
            });
            myRequests = [res.request, ...myRequests];
            topupCrc = ''; topupNote = '';
            message = { type: 'success', text: `Solicitud enviada. Haz el SINPE al ${res.sinpe_number} y envía el comprobante por WhatsApp.` };
        } catch (e) {
            message = { type: 'error', text: e.message };
        } finally {
            requestingKind = '';
            setTimeout(() => message = { type: '', text: '' }, 6000);
        }
    }

    async function submitPackageRequest() {
        if (!requestPackageId) return;
        requestingKind = 'package';
        try {
            const res = await billingAPI.createRequest({ kind: 'package', package_id: Number(requestPackageId) });
            myRequests = [res.request, ...myRequests];
            requestPackageId = '';
            message = { type: 'success', text: `Solicitud enviada. Haz el SINPE al ${res.sinpe_number} y envía el comprobante por WhatsApp.` };
        } catch (e) {
            message = { type: 'error', text: e.message };
        } finally {
            requestingKind = '';
            setTimeout(() => message = { type: '', text: '' }, 6000);
        }
    }

    const REQUEST_STATUS = {
        pending: { label: 'Pendiente', cls: 'pending' },
        approved: { label: 'Aprobada', cls: 'approved' },
        rejected: { label: 'Rechazada', cls: 'rejected' }
    };

    async function doExport(kind) {
        exporting = kind;
        try {
            if (kind === 'contacts-csv') await dataAPI.exportContacts('csv');
            else if (kind === 'contacts-json') await dataAPI.exportContacts('json');
            else await dataAPI.exportConversations();
        } catch (e) {
            message = { type: 'error', text: e.message };
            setTimeout(() => message = { type: '', text: '' }, 4000);
        } finally {
            exporting = '';
        }
    }

    async function handleImportFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        importing = true;
        importResult = null;
        try {
            importResult = await dataAPI.importConversations(file);
            message = { type: 'success', text: 'Importación completada' };
        } catch (err) {
            message = { type: 'error', text: err.message };
        } finally {
            importing = false;
            e.target.value = '';
            setTimeout(() => message = { type: '', text: '' }, 4000);
        }
    }

    function fmtUsd(v) {
        return `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
    }
    function fmtCrc(v) {
        return `₡${Math.round(Number(v) || 0).toLocaleString('es-CR')}`;
    }
    function fmtTxDate(iso) {
        try { return new Date(iso).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
        catch { return iso; }
    }

    // Profile settings
    let profile = {
        name: $currentUser?.name || '',
        email: $currentUser?.email || '',
        avatar_url: $currentUser?.avatar_url || ''
    };

    // Password
    let passwords = {
        current: '',
        new: '',
        confirm: ''
    };

    // Preferences - load from user or localStorage fallback
    let preferences = {
        language: $currentUser?.preferences?.language || localStorage.getItem('language') || 'es',
        timezone: $currentUser?.preferences?.timezone || localStorage.getItem('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone,
        theme: $currentUser?.preferences?.theme || localStorage.getItem('theme') || 'light',
        primaryColor: $currentUser?.preferences?.primaryColor || localStorage.getItem('primaryColor') || '#2196f3'
    };

    // Notification settings
    let notificationSettings = {
        soundEnabled: notificationService.soundEnabled,
        desktopEnabled: notificationService.notificationsEnabled,
        permissionStatus: typeof Notification !== 'undefined' ? Notification.permission : 'denied'
    };

    // Timezone options
    const timezones = [
        { value: 'America/New_York', label: 'Eastern Time (US)' },
        { value: 'America/Chicago', label: 'Central Time (US)' },
        { value: 'America/Denver', label: 'Mountain Time (US)' },
        { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
        { value: 'America/Mexico_City', label: 'Mexico City' },
        { value: 'America/Bogota', label: 'Bogota, Lima' },
        { value: 'America/Caracas', label: 'Caracas' },
        { value: 'America/Santiago', label: 'Santiago' },
        { value: 'America/Buenos_Aires', label: 'Buenos Aires' },
        { value: 'America/Sao_Paulo', label: 'Sao Paulo' },
        { value: 'Europe/London', label: 'London' },
        { value: 'Europe/Paris', label: 'Paris, Madrid' },
        { value: 'Europe/Berlin', label: 'Berlin' },
        { value: 'UTC', label: 'UTC' }
    ];

    // Translations
    const translations = {
        es: {
            settings: 'Configuracion',
            profile: 'Perfil',
            security: 'Seguridad',
            preferences: 'Preferencias',
            appearance: 'Apariencia',
            name: 'Nombre',
            email: 'Correo',
            avatar: 'URL del Avatar',
            saveProfile: 'Guardar Perfil',
            currentPassword: 'Contrasena Actual',
            newPassword: 'Nueva Contrasena',
            confirmPassword: 'Confirmar Contrasena',
            changePassword: 'Cambiar Contrasena',
            language: 'Idioma',
            timezone: 'Zona Horaria',
            theme: 'Tema',
            light: 'Claro',
            dark: 'Oscuro',
            primaryColor: 'Color Principal',
            savePreferences: 'Guardar Preferencias',
            saved: 'Guardado correctamente',
            error: 'Error al guardar',
            passwordMismatch: 'Las contrasenas no coinciden',
            passwordChanged: 'Contrasena cambiada correctamente',
            currentTime: 'Hora actual',
            notifications: 'Notificaciones',
            soundNotifications: 'Sonido de Notificación',
            desktopNotifications: 'Notificaciones de Escritorio',
            testNotification: 'Probar Notificación',
            enableSound: 'Activar sonido cuando lleguen mensajes',
            enableDesktop: 'Mostrar notificaciones en el escritorio',
            permissionGranted: 'Permisos otorgados',
            permissionDenied: 'Permisos denegados - habilite en su navegador',
            permissionDefault: 'Click para solicitar permisos',
            requestPermission: 'Solicitar Permisos',
            data: 'Datos',
            exportTitle: 'Exportar mis datos',
            exportDesc: 'Descarga los datos de tu organización. El backup excluye claves de API y secretos.',
            exportContacts: 'Contactos (CSV)',
            exportChats: 'Conversaciones (CSV)',
            exportBackup: 'Backup completo (JSON)',
            exportZip: 'Backup con adjuntos (ZIP)',
            downloading: 'Descargando…',
            downloadFailed: 'No se pudo descargar'
        },
        en: {
            settings: 'Settings',
            profile: 'Profile',
            security: 'Security',
            preferences: 'Preferences',
            appearance: 'Appearance',
            name: 'Name',
            email: 'Email',
            avatar: 'Avatar URL',
            saveProfile: 'Save Profile',
            currentPassword: 'Current Password',
            newPassword: 'New Password',
            confirmPassword: 'Confirm Password',
            changePassword: 'Change Password',
            language: 'Language',
            timezone: 'Timezone',
            theme: 'Theme',
            light: 'Light',
            dark: 'Dark',
            primaryColor: 'Primary Color',
            savePreferences: 'Save Preferences',
            saved: 'Saved successfully',
            error: 'Error saving',
            passwordMismatch: 'Passwords do not match',
            passwordChanged: 'Password changed successfully',
            currentTime: 'Current time',
            notifications: 'Notifications',
            soundNotifications: 'Sound Notification',
            desktopNotifications: 'Desktop Notifications',
            testNotification: 'Test Notification',
            enableSound: 'Play sound when messages arrive',
            enableDesktop: 'Show desktop notifications',
            permissionGranted: 'Permission granted',
            permissionDenied: 'Permission denied - enable in your browser',
            permissionDefault: 'Click to request permission',
            requestPermission: 'Request Permission',
            data: 'Data',
            exportTitle: 'Export my data',
            exportDesc: 'Download your organization data. The backup excludes API keys and secrets.',
            exportContacts: 'Contacts (CSV)',
            exportChats: 'Conversations (CSV)',
            exportBackup: 'Full backup (JSON)',
            exportZip: 'Backup with attachments (ZIP)',
            downloading: 'Downloading…',
            downloadFailed: 'Download failed'
        }
    };

    $: t = translations[preferences.language] || translations.es;
    $: currentTime = new Date().toLocaleString(preferences.language === 'es' ? 'es-ES' : 'en-US', {
        timeZone: preferences.timezone,
        dateStyle: 'full',
        timeStyle: 'long'
    });

    // Update time every second
    let timeInterval;
    import { onMount, onDestroy } from 'svelte';
    onMount(() => {
        timeInterval = setInterval(() => {
            currentTime = new Date().toLocaleString(preferences.language === 'es' ? 'es-ES' : 'en-US', {
                timeZone: preferences.timezone,
                dateStyle: 'full',
                timeStyle: 'long'
            });
        }, 1000);
    });
    onDestroy(() => clearInterval(timeInterval));

    async function saveProfile() {
        saving = true;
        message = { type: '', text: '' };
        try {
            const res = await api.put('/users/me', profile);
            currentUser.set(res);
            message = { type: 'success', text: t.saved };
        } catch (err) {
            message = { type: 'error', text: err.message || t.error };
        }
        saving = false;
    }

    async function changePassword() {
        if (passwords.new !== passwords.confirm) {
            message = { type: 'error', text: t.passwordMismatch };
            return;
        }
        saving = true;
        message = { type: '', text: '' };
        try {
            await api.put('/users/me/password', {
                current_password: passwords.current,
                new_password: passwords.new
            });
            message = { type: 'success', text: t.passwordChanged };
            passwords = { current: '', new: '', confirm: '' };
        } catch (err) {
            message = { type: 'error', text: err.message || t.error };
        }
        saving = false;
    }

    async function savePreferences() {
        saving = true;
        message = { type: '', text: '' };

        // Save to localStorage for immediate effect
        localStorage.setItem('language', preferences.language);
        localStorage.setItem('timezone', preferences.timezone);
        localStorage.setItem('theme', preferences.theme);
        localStorage.setItem('primaryColor', preferences.primaryColor);

        // Apply theme immediately
        document.documentElement.setAttribute('data-theme', preferences.theme);
        document.documentElement.style.setProperty('--primary-color', preferences.primaryColor);

        // Save to backend
        try {
            const res = await api.put('/users/me', { preferences });
            currentUser.set(res);
            message = { type: 'success', text: t.saved };
        } catch (err) {
            message = { type: 'error', text: err.message || t.error };
        }
        saving = false;
    }

    function applyTheme() {
        document.documentElement.setAttribute('data-theme', preferences.theme);
        document.documentElement.style.setProperty('--primary-color', preferences.primaryColor);
    }

    async function toggleSound() {
        notificationService.toggleSound(notificationSettings.soundEnabled);
        message = { type: 'success', text: t.saved };
        setTimeout(() => message = { type: '', text: '' }, 2000);
    }

    async function toggleDesktopNotifications() {
        const enabled = await notificationService.toggleNotifications(notificationSettings.desktopEnabled);
        notificationSettings.desktopEnabled = enabled;
        notificationSettings.permissionStatus = Notification.permission;
        message = { type: 'success', text: t.saved };
        setTimeout(() => message = { type: '', text: '' }, 2000);
    }

    async function requestNotificationPermission() {
        const granted = await notificationService.requestPermission();
        notificationSettings.desktopEnabled = granted;
        notificationSettings.permissionStatus = Notification.permission;
        if (granted) {
            message = { type: 'success', text: t.permissionGranted };
        }
        setTimeout(() => message = { type: '', text: '' }, 2000);
    }

    function testNotifications() {
        notificationService.testNotification();
    }

    onMount(() => {
        applyTheme();
        if ($currentUser) {
            profile.name = $currentUser.name;
            profile.email = $currentUser.email;
            profile.avatar_url = $currentUser.avatar_url || '';
        }
    });
</script>

<div class="settings-container">
    <h1>{t.settings}</h1>

    {#if message.text}
        <div class="message {message.type}">{message.text}</div>
    {/if}

    <div class="settings-layout">
        <nav class="settings-nav">
            <button class:active={activeTab === 'profile'} on:click={() => activeTab = 'profile'}>
                <span class="icon">👤</span> {t.profile}
            </button>
            <button class:active={activeTab === 'security'} on:click={() => activeTab = 'security'}>
                <span class="icon">🔒</span> {t.security}
            </button>
            <button class:active={activeTab === 'notifications'} on:click={() => activeTab = 'notifications'}>
                <span class="icon">🔔</span> {t.notifications}
            </button>
            <button class:active={activeTab === 'preferences'} on:click={() => activeTab = 'preferences'}>
                <span class="icon">🌐</span> {t.preferences}
            </button>
            <button class:active={activeTab === 'appearance'} on:click={() => activeTab = 'appearance'}>
                <span class="icon">🎨</span> {t.appearance}
            </button>
            <button class:active={activeTab === 'data'} on:click={() => { activeTab = 'data'; loadDataTab(); }}>
                <span class="icon">💳</span> Datos & Créditos
            </button>
        </nav>

        <div class="settings-content">
            {#if activeTab === 'profile'}
                <div class="settings-section">
                    <h2>{t.profile}</h2>
                    <form on:submit|preventDefault={saveProfile}>
                        <div class="form-group">
                            <label>{t.name}</label>
                            <input type="text" bind:value={profile.name} required />
                        </div>
                        <div class="form-group">
                            <label>{t.email}</label>
                            <input type="email" bind:value={profile.email} required />
                        </div>
                        <div class="form-group">
                            <label>{t.avatar}</label>
                            <input type="url" bind:value={profile.avatar_url} placeholder="https://..." />
                        </div>
                        <button type="submit" class="btn-primary" disabled={saving}>
                            {saving ? '...' : t.saveProfile}
                        </button>
                    </form>
                </div>

            {:else if activeTab === 'security'}
                <div class="settings-section">
                    <h2>{t.security}</h2>
                    <form on:submit|preventDefault={changePassword}>
                        <div class="form-group">
                            <label>{t.currentPassword}</label>
                            <input type="password" bind:value={passwords.current} required />
                        </div>
                        <div class="form-group">
                            <label>{t.newPassword}</label>
                            <input type="password" bind:value={passwords.new} required minlength="6" />
                        </div>
                        <div class="form-group">
                            <label>{t.confirmPassword}</label>
                            <input type="password" bind:value={passwords.confirm} required />
                        </div>
                        <button type="submit" class="btn-primary" disabled={saving}>
                            {saving ? '...' : t.changePassword}
                        </button>
                    </form>
                </div>

            {:else if activeTab === 'notifications'}
                <div class="settings-section">
                    <h2>{t.notifications}</h2>

                    <div class="notification-status">
                        <span class="icon">🔔</span>
                        <div>
                            <strong>Estado de Permisos:</strong>
                            {#if notificationSettings.permissionStatus === 'granted'}
                                <span class="status-badge granted">✓ {t.permissionGranted}</span>
                            {:else if notificationSettings.permissionStatus === 'denied'}
                                <span class="status-badge denied">✗ {t.permissionDenied}</span>
                            {:else}
                                <span class="status-badge default">⚠ {t.permissionDefault}</span>
                            {/if}
                        </div>
                    </div>

                    {#if notificationSettings.permissionStatus !== 'granted'}
                        <div class="permission-request">
                            <p>Para recibir notificaciones de nuevos mensajes, debes otorgar permisos:</p>
                            <button class="btn-primary" on:click={requestNotificationPermission}>
                                {t.requestPermission}
                            </button>
                        </div>
                    {/if}

                    <div class="form-group">
                        <label class="toggle-label">
                            <input
                                type="checkbox"
                                bind:checked={notificationSettings.soundEnabled}
                                on:change={toggleSound}
                            />
                            <span class="toggle-text">
                                <strong>{t.soundNotifications}</strong>
                                <small>{t.enableSound}</small>
                            </span>
                        </label>
                    </div>

                    <div class="form-group">
                        <label class="toggle-label">
                            <input
                                type="checkbox"
                                bind:checked={notificationSettings.desktopEnabled}
                                on:change={toggleDesktopNotifications}
                                disabled={notificationSettings.permissionStatus !== 'granted'}
                            />
                            <span class="toggle-text">
                                <strong>{t.desktopNotifications}</strong>
                                <small>{t.enableDesktop}</small>
                            </span>
                        </label>
                    </div>

                    <div class="test-notification">
                        <button class="btn-secondary" on:click={testNotifications}>
                            🔔 {t.testNotification}
                        </button>
                        <small>Reproduce un sonido y muestra una notificación de prueba</small>
                    </div>
                </div>

            {:else if activeTab === 'preferences'}
                <div class="settings-section">
                    <h2>{t.preferences}</h2>

                    <div class="time-display">
                        <span class="icon">🕐</span>
                        <div>
                            <small>{t.currentTime}:</small>
                            <strong>{currentTime}</strong>
                        </div>
                    </div>

                    <form on:submit|preventDefault={savePreferences}>
                        <div class="form-group">
                            <label>{t.language}</label>
                            <select bind:value={preferences.language}>
                                <option value="es">Espanol</option>
                                <option value="en">English</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>{t.timezone}</label>
                            <select bind:value={preferences.timezone}>
                                {#each timezones as tz}
                                    <option value={tz.value}>{tz.label}</option>
                                {/each}
                            </select>
                        </div>
                        <button type="submit" class="btn-primary">
                            {t.savePreferences}
                        </button>
                    </form>
                </div>

            {:else if activeTab === 'appearance'}
                <div class="settings-section">
                    <h2>{t.appearance}</h2>
                    <form on:submit|preventDefault={savePreferences}>
                        <div class="form-group">
                            <label>{t.theme}</label>
                            <div class="theme-options">
                                <label class="theme-option" class:selected={preferences.theme === 'light'}>
                                    <input type="radio" bind:group={preferences.theme} value="light" />
                                    <span class="theme-preview light"></span>
                                    <span>{t.light}</span>
                                </label>
                                <label class="theme-option" class:selected={preferences.theme === 'dark'}>
                                    <input type="radio" bind:group={preferences.theme} value="dark" />
                                    <span class="theme-preview dark"></span>
                                    <span>{t.dark}</span>
                                </label>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>{t.primaryColor}</label>
                            <div class="color-picker">
                                <input type="color" bind:value={preferences.primaryColor} />
                                <span>{preferences.primaryColor}</span>
                            </div>
                        </div>
                        <button type="submit" class="btn-primary" on:click={applyTheme}>
                            {t.savePreferences}
                        </button>
                    </form>
                </div>
            {:else if activeTab === 'data'}
                <div class="settings-section">
                    <h2>Créditos de IA</h2>
                    {#if billingBalance}
                        <div class="credit-summary" class:low={billingBalance.low_balance}>
                            <div>
                                <div class="credit-amount">{fmtCrc(billingBalance.balance_crc)}</div>
                                <div class="credit-sub">
                                    {fmtUsd(billingBalance.balance_usd)} ·
                                    Consumido este mes: {fmtCrc(billingBalance.consumed_this_month_usd * billingBalance.exchange_rate_crc)}
                                </div>
                            </div>
                            {#if billingBalance.low_balance}
                                <span class="credit-warning">⚠️ Saldo bajo</span>
                            {/if}
                        </div>
                        <p class="hint">Cuando el saldo llega a cero, la IA de Mangu se pausa hasta la próxima recarga (tus proveedores propios no se ven afectados).</p>

                        {#if paymentInfo}
                            <div class="sinpe-card">
                                <div class="sinpe-head">
                                    <span class="sinpe-icon">📲</span>
                                    <div>
                                        <b>Recarga por SINPE Móvil: {paymentInfo.sinpe_number}</b>
                                        <div class="sinpe-sub">
                                            Mínimo {fmtCrc(paymentInfo.min_topup_crc)}. Hacé el SINPE, solicitá la recarga aquí y
                                            <a href={waReceiptLink} target="_blank" rel="noopener noreferrer">enviá el comprobante por WhatsApp</a>.
                                            Tu asesor la aprueba y los créditos quedan disponibles.
                                        </div>
                                    </div>
                                </div>
                                <form class="sinpe-form" on:submit|preventDefault={submitTopupRequest}>
                                    <input type="number" min={paymentInfo.min_topup_crc} step="500" required
                                        bind:value={topupCrc} placeholder="Monto en ₡ (mín. {Math.round(paymentInfo.min_topup_crc).toLocaleString('es-CR')})" />
                                    <input type="text" bind:value={topupNote} placeholder="Nº de comprobante SINPE (opcional)" />
                                    <button type="submit" class="btn-primary" disabled={requestingKind === 'topup'}>
                                        {requestingKind === 'topup' ? 'Enviando...' : 'Solicitar recarga'}
                                    </button>
                                </form>

                                {#if sellablePackages.length}
                                    <div class="pkg-request">
                                        <div class="sinpe-sub">¿Querés cambiar de plan? Solicitá un paquete y pagalo igual por SINPE:</div>
                                        <div class="pkg-request-row">
                                            <select bind:value={requestPackageId}>
                                                <option value="">Elegir paquete...</option>
                                                {#each sellablePackages as p}
                                                    <option value={p.id}>{p.name} — {fmtCrc(p.price_crc)}/mes</option>
                                                {/each}
                                            </select>
                                            <button class="btn-primary" disabled={!requestPackageId || requestingKind === 'package'}
                                                on:click={submitPackageRequest}>
                                                {requestingKind === 'package' ? 'Enviando...' : 'Solicitar paquete'}
                                            </button>
                                        </div>
                                    </div>
                                {/if}
                            </div>

                            {#if myRequests.length}
                                <h3 class="sub-title">Mis solicitudes</h3>
                                <table class="tx-table">
                                    <thead>
                                        <tr><th>Fecha</th><th>Tipo</th><th>Monto</th><th>Estado</th></tr>
                                    </thead>
                                    <tbody>
                                        {#each myRequests as r (r.id)}
                                            <tr>
                                                <td>{fmtTxDate(r.created_at)}</td>
                                                <td>{r.kind === 'topup' ? 'Recarga IA' : `Paquete ${r.package_name || ''}`}</td>
                                                <td>{r.amount_crc ? fmtCrc(r.amount_crc) : '—'}</td>
                                                <td>
                                                    <span class="req-badge {REQUEST_STATUS[r.status]?.cls}">{REQUEST_STATUS[r.status]?.label || r.status}</span>
                                                    {#if r.review_note}<div class="sinpe-sub">{r.review_note}</div>{/if}
                                                </td>
                                            </tr>
                                        {/each}
                                    </tbody>
                                </table>
                            {/if}
                        {/if}

                        {#if billingTxs.items.length}
                            <h3 class="sub-title">Movimientos</h3>
                            <table class="tx-table">
                                <thead>
                                    <tr><th>Fecha</th><th>Tipo</th><th>Monto</th><th>Detalle</th></tr>
                                </thead>
                                <tbody>
                                    {#each billingTxs.items as tx (tx.id)}
                                        <tr>
                                            <td>{fmtTxDate(tx.created_at)}</td>
                                            <td>{tx.kind === 'topup' ? 'Recarga' : tx.kind === 'consumption' ? 'Consumo' : 'Ajuste'}</td>
                                            <td class:positive={tx.amount_usd > 0}>{fmtUsd(tx.amount_usd)}</td>
                                            <td class="muted">{tx.description || (tx.model_name ? `${tx.model_name} · ${tx.tokens} tokens` : '—')}</td>
                                        </tr>
                                    {/each}
                                </tbody>
                            </table>
                            {#if billingTxs.total > billingTxs.per_page}
                                <div class="tx-pager">
                                    <button class="btn-secondary-sm" disabled={billingTxs.page <= 1}
                                        on:click={() => loadTxPage(billingTxs.page - 1)}>← Anterior</button>
                                    <span>Página {billingTxs.page}</span>
                                    <button class="btn-secondary-sm" disabled={billingTxs.page * billingTxs.per_page >= billingTxs.total}
                                        on:click={() => loadTxPage(billingTxs.page + 1)}>Siguiente →</button>
                                </div>
                            {/if}
                        {:else}
                            <p class="muted">Sin movimientos todavía.</p>
                        {/if}
                    {:else}
                        <p class="muted">No se pudo cargar el saldo.</p>
                    {/if}
                </div>

                <div class="settings-section">
                    <h2>Exportar mis datos</h2>
                    <p class="hint">Descarga una copia de tus datos. Los archivos sirven para respaldos o para importarlos en otra cuenta Mangu.</p>
                    <div class="export-buttons">
                        <button class="btn-primary" disabled={exporting === 'contacts-csv'} on:click={() => doExport('contacts-csv')}>
                            {exporting === 'contacts-csv' ? 'Exportando...' : '⬇ Contactos (CSV)'}
                        </button>
                        <button class="btn-primary" disabled={exporting === 'contacts-json'} on:click={() => doExport('contacts-json')}>
                            {exporting === 'contacts-json' ? 'Exportando...' : '⬇ Contactos (JSON)'}
                        </button>
                        <button class="btn-primary" disabled={exporting === 'conversations'} on:click={() => doExport('conversations')}>
                            {exporting === 'conversations' ? 'Exportando...' : '⬇ Conversaciones (JSON)'}
                        </button>
                    </div>
                </div>

                <div class="settings-section">
                    <h2>Importar conversaciones</h2>
                    <p class="hint">Sube un archivo de exportación Mangu (.json). Los contactos se emparejan por email/teléfono y los mensajes conservan sus fechas originales.</p>
                    <label class="import-drop">
                        <input type="file" accept=".json,application/json" on:change={handleImportFile} disabled={importing} />
                        <span>{importing ? 'Importando...' : '📂 Seleccionar archivo de exportación (.json)'}</span>
                    </label>
                    {#if importResult}
                        <div class="import-result">
                            <div>✅ Conversaciones: <b>{importResult.conversations_created}</b></div>
                            <div>👤 Contactos creados: <b>{importResult.contacts_created}</b> · emparejados: <b>{importResult.contacts_matched}</b></div>
                            <div>💬 Mensajes: <b>{importResult.messages_created}</b></div>
                            {#if importResult.errors?.length}
                                <div class="import-errors">⚠️ {importResult.errors.length} error(es)</div>
                            {/if}
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    </div>
</div>

<style>
    .settings-container {
        padding: 20px;
        max-width: 900px;
        margin: 0 auto;
    }

    /* Datos & Créditos */
    .credit-summary {
        display: flex; align-items: center; justify-content: space-between;
        padding: 16px 18px;
        border: 1px solid var(--border-1);
        border-radius: 10px;
        background: var(--surface-1);
        margin-bottom: 10px;
    }
    .credit-summary.low { border-color: var(--warning); background: var(--warning-soft); }
    .credit-amount { font-size: 26px; font-weight: 800; color: var(--text-1); }
    .credit-sub { font-size: 12.5px; color: var(--text-2); margin-top: 2px; }
    .credit-warning { font-weight: 700; color: #b45309; font-size: 13px; }
    .hint { font-size: 12.5px; color: var(--text-3); margin-bottom: 14px; line-height: 1.5; }
    .muted { color: var(--text-3); }

    .sinpe-card {
        border: 1px solid var(--border-1);
        border-radius: 10px;
        background: var(--surface-1);
        padding: 14px 16px;
        margin-bottom: 16px;
    }
    .sinpe-head { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 10px; }
    .sinpe-icon { font-size: 22px; }
    .sinpe-sub { font-size: 12.5px; color: var(--text-3); margin-top: 2px; line-height: 1.5; }
    .sinpe-sub a { color: var(--primary-color, #2196f3); font-weight: 600; }
    .sinpe-form { display: flex; gap: 8px; flex-wrap: wrap; }
    .sinpe-form input {
        flex: 1; min-width: 150px;
        padding: 9px 11px;
        border: 1px solid #ddd; border-radius: 8px; font-size: 13.5px;
    }
    .pkg-request { margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--border-1); }
    .pkg-request-row { display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
    .pkg-request-row select {
        flex: 1; min-width: 200px;
        padding: 9px 11px;
        border: 1px solid #ddd; border-radius: 8px; font-size: 13.5px;
    }
    .sub-title { font-size: 14px; margin: 14px 0 8px; color: var(--text-2); }
    .req-badge {
        display: inline-block; padding: 2px 10px;
        border-radius: 10px; font-size: 12px; font-weight: 600;
    }
    .req-badge.pending { background: #fff3e0; color: #e65100; }
    .req-badge.approved { background: #e8f5e9; color: #2e7d32; }
    .req-badge.rejected { background: #ffebee; color: #c62828; }

    .tx-table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .tx-table th {
        text-align: left; padding: 8px 10px;
        font-size: 11px; font-weight: 700; color: var(--text-3);
        text-transform: uppercase; letter-spacing: 0.04em;
        border-bottom: 1px solid var(--border-1);
    }
    .tx-table td { padding: 8px 10px; border-bottom: 1px solid var(--border-1); }
    .tx-table td.positive { color: var(--success); font-weight: 600; }
    .tx-pager {
        display: flex; align-items: center; justify-content: space-between;
        margin-top: 10px; font-size: 12.5px; color: var(--text-3);
    }
    .btn-secondary-sm {
        padding: 5px 12px;
        border: 1px solid var(--border-2);
        border-radius: 6px;
        background: var(--surface-0);
        font-size: 12.5px;
        cursor: pointer;
    }
    .btn-secondary-sm:disabled { opacity: 0.5; cursor: not-allowed; }

    .export-buttons { display: flex; gap: 10px; flex-wrap: wrap; }

    .import-drop {
        display: block;
        padding: 22px;
        border: 2px dashed var(--border-2);
        border-radius: 10px;
        text-align: center;
        cursor: pointer;
        color: var(--text-2);
        font-size: 13.5px;
        transition: border-color 0.15s, background 0.15s;
    }
    .import-drop:hover { border-color: var(--accent); background: var(--accent-soft); }
    .import-drop input[type="file"] { display: none; }
    .import-result {
        margin-top: 12px;
        padding: 12px 14px;
        border-radius: 8px;
        background: var(--success-soft);
        font-size: 13px;
        display: flex; flex-direction: column; gap: 4px;
    }
    .import-errors { color: #b45309; }

    h1 {
        margin-bottom: 20px;
        color: var(--primary-color, #2196f3);
    }

    .message {
        padding: 12px;
        border-radius: 8px;
        margin-bottom: 20px;
    }

    .message.success {
        background: #e8f5e9;
        color: #2e7d32;
    }

    .message.error {
        background: #ffebee;
        color: #c62828;
    }

    .settings-layout {
        display: grid;
        grid-template-columns: 200px 1fr;
        gap: 30px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        overflow: hidden;
    }

    .settings-nav {
        background: #f5f5f5;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .settings-nav button {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        border: none;
        background: transparent;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
        color: #666;
        transition: all 0.2s;
    }

    .settings-nav button:hover {
        background: #e0e0e0;
    }

    .settings-nav button.active {
        background: var(--primary-color, #2196f3);
        color: white;
    }

    .settings-content {
        padding: 30px;
    }

    .settings-section h2 {
        margin-bottom: 24px;
        color: #333;
    }

    .form-group {
        margin-bottom: 20px;
    }

    .form-group label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #555;
    }

    .form-group input[type="text"],
    .form-group input[type="email"],
    .form-group input[type="password"],
    .form-group input[type="url"],
    .form-group select {
        width: 100%;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    .form-group input:focus,
    .form-group select:focus {
        outline: none;
        border-color: var(--primary-color, #2196f3);
    }

    .btn-primary {
        background: var(--primary-color, #2196f3);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
    }

    .btn-primary:hover {
        opacity: 0.9;
    }

    .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .time-display {
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        margin-bottom: 24px;
    }

    .time-display .icon {
        font-size: 32px;
    }

    .time-display small {
        display: block;
        color: #666;
        margin-bottom: 4px;
    }

    .time-display strong {
        font-size: 16px;
        color: #333;
    }

    .theme-options {
        display: flex;
        gap: 20px;
    }

    .theme-option {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        padding: 15px;
        border: 2px solid #ddd;
        border-radius: 12px;
        transition: all 0.2s;
    }

    .theme-option.selected {
        border-color: var(--primary-color, #2196f3);
    }

    .theme-option input {
        display: none;
    }

    .theme-preview {
        width: 80px;
        height: 50px;
        border-radius: 8px;
    }

    .theme-preview.light {
        background: linear-gradient(to bottom, #fff 50%, #f5f5f5 50%);
        border: 1px solid #ddd;
    }

    .theme-preview.dark {
        background: linear-gradient(to bottom, #1a1a1a 50%, #2d2d2d 50%);
    }

    .color-picker {
        display: flex;
        align-items: center;
        gap: 15px;
    }

    .color-picker input[type="color"] {
        width: 60px;
        height: 40px;
        border: none;
        border-radius: 8px;
        cursor: pointer;
    }

    .color-picker span {
        font-family: monospace;
        color: #666;
    }

    @media (max-width: 768px) {
        .settings-layout {
            grid-template-columns: 1fr;
        }

        .settings-nav {
            flex-direction: row;
            overflow-x: auto;
        }
    }

    :global([data-theme="dark"]) .settings-container {
        color: #eee;
    }

    :global([data-theme="dark"]) .settings-layout {
        background: #2d2d2d;
    }

    :global([data-theme="dark"]) .settings-nav {
        background: #1a1a1a;
    }

    :global([data-theme="dark"]) .settings-nav button {
        color: #ccc;
    }

    :global([data-theme="dark"]) .settings-nav button:hover {
        background: #333;
    }

    :global([data-theme="dark"]) .settings-section h2,
    :global([data-theme="dark"]) .form-group label {
        color: #eee;
    }

    :global([data-theme="dark"]) .form-group input,
    :global([data-theme="dark"]) .form-group select {
        background: #1a1a1a;
        border-color: #444;
        color: #eee;
    }

    :global([data-theme="dark"]) .time-display {
        background: #1a1a1a;
    }

    :global([data-theme="dark"]) .time-display strong {
        color: #eee;
    }

    /* Notification Settings */
    .notification-status {
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        margin-bottom: 24px;
    }

    .notification-status .icon {
        font-size: 32px;
    }

    .notification-status strong {
        display: block;
        margin-bottom: 8px;
        color: #333;
    }

    .status-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
    }

    .status-badge.granted {
        background: #e8f5e9;
        color: #2e7d32;
    }

    .status-badge.denied {
        background: #ffebee;
        color: #c62828;
    }

    .status-badge.default {
        background: #fff3e0;
        color: #e65100;
    }

    .permission-request {
        padding: 20px;
        background: #fff3e0;
        border-left: 4px solid #ff9800;
        border-radius: 8px;
        margin-bottom: 24px;
    }

    .permission-request p {
        margin: 0 0 15px 0;
        color: #666;
    }

    .toggle-label {
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 15px;
        border: 2px solid #e0e0e0;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s;
    }

    .toggle-label:hover {
        border-color: var(--primary-color, #128C7E);
        background: #f8f9fa;
    }

    .toggle-label input[type="checkbox"] {
        width: 20px;
        height: 20px;
        cursor: pointer;
    }

    .toggle-label input[type="checkbox"]:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    .toggle-text {
        flex: 1;
    }

    .toggle-text strong {
        display: block;
        color: #333;
        margin-bottom: 4px;
    }

    .toggle-text small {
        display: block;
        color: #666;
        font-size: 13px;
    }

    .test-notification {
        margin-top: 32px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: center;
    }

    .test-notification small {
        display: block;
        margin-top: 10px;
        color: #666;
    }

    .btn-secondary {
        background: #f0f0f0;
        color: #333;
        border: 2px solid #ddd;
        padding: 12px 24px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s;
    }

    .btn-secondary:hover {
        background: #e0e0e0;
        border-color: #ccc;
    }

    :global([data-theme="dark"]) .notification-status {
        background: #1a1a1a;
    }

    :global([data-theme="dark"]) .notification-status strong {
        color: #eee;
    }

    :global([data-theme="dark"]) .toggle-label {
        border-color: #444;
    }

    :global([data-theme="dark"]) .toggle-label:hover {
        background: #2d2d2d;
    }

    :global([data-theme="dark"]) .toggle-text strong {
        color: #eee;
    }

    :global([data-theme="dark"]) .toggle-text small {
        color: #aaa;
    }

    :global([data-theme="dark"]) .test-notification {
        background: #1a1a1a;
    }

    :global([data-theme="dark"]) .btn-secondary {
        background: #2d2d2d;
        color: #eee;
        border-color: #444;
    }

    :global([data-theme="dark"]) .btn-secondary:hover {
        background: #3d3d3d;
        border-color: #555;
    }

    .export-desc {
        color: #666;
        font-size: 14px;
        margin: 8px 0 16px;
    }

    .export-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 12px;
    }

    .export-btn {
        padding: 14px 18px;
        font-size: 14px;
        text-align: left;
        cursor: pointer;
    }

    .export-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    :global([data-theme="dark"]) .export-desc {
        color: #aaa;
    }
</style>
